import apiClient from './api.service';

export interface MaterialIssuanceItem {
  id: string;
  material_request_item_id: string;
  item_id: string;
  item_code?: string;
  item_name?: string;
  issued_quantity: number;
  base_quantity?: number;
  area_dimension?: number;
  /** Snapshot inherited from the MR line */
  measurement_type?: string | null;
  length_mm?: number | string | null;
  width_mm?: number | string | null;
  height_mm?: number | string | null;
  uom: string;
  unit_cost: number;
  total_cost: number;
  batch_number?: string;
  heat_number?: string;
  dimension?: string;
  mtc_document_id?: string | null;
  mtc_document_number?: string | null;
  notes?: string;
  work_order_line_item_id?: string | null;
  boq_line_item_id?: string | null;
  boq_line_label?: string | null;
}

export interface MaterialIssuance {
  id: string;
  issuance_number: string;
  company_id: string;
  material_request_id: string;
  work_order_id: string;
  issuance_date: string;
  issuance_type: 'Full' | 'Partial';
  issuance_status?: string;
  approval_status?: string | null;
  approval_instance_id?: string | null;
  approved_by?: string | null;
  approved_date?: string | null;
  source_warehouse_id: string;
  department_id?: string | null;
  total_cost: number;
  issued_by: string;
  issued_by_name?: string | null;
  received_by?: string;
  received_by_name?: string | null;
  notes?: string;
  journal_voucher_id?: string | null;
  created_at: string;
  updated_at: string;
  items: MaterialIssuanceItem[];
  work_order_number?: string | null;
  source_warehouse_summary?: {
    id: string;
    warehouse_code?: string;
    warehouse_name?: string;
  };
  project_summary?: {
    id: string;
    project_number?: string | null;
    project_name?: string | null;
  };
  journal_voucher_summary?: {
    id: string;
    voucher_number: string;
  };
  materialRequest?: {
    id: string;
    request_number?: string;
    work_order_id?: string;
    work_order_number?: string;
    department_id?: string | null;
  };
}

export interface IssueMaterialsDTO {
  material_request_id: string;
  issuance_type: 'Full' | 'Partial';
  source_warehouse_id: string;
  department_id?: string;
  received_by?: string;
  notes?: string;
  items: Array<{
    material_request_item_id: string;
    item_id: string;
    issued_quantity: number;
    base_quantity?: number;
    area_dimension?: number;
    uom: string;
    unit_cost?: number;
    batch_number?: string;
    heat_number?: string;
    dimension?: string;
    mtc_document_id?: string | null;
    notes?: string;
  }>;
}

export type CreateMaterialIssuanceDTO = IssueMaterialsDTO;

class MaterialIssuanceService {
  async getAll(filters?: {
    warehouseId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<MaterialIssuance[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.warehouseId) params.append('warehouseId', filters.warehouseId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);

      const response = await apiClient.get(`/inventory/material-issuances?${params.toString()}`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching material issuances:', error);
      return [];
    }
  }

  async getById(id: string): Promise<MaterialIssuance> {
    const response = await apiClient.get(`/inventory/material-issuances/${id}`);
    return response.data;
  }

  async getByWorkOrder(workOrderId: string): Promise<MaterialIssuance[]> {
    try {
      const response = await apiClient.get(`/inventory/material-issuances/work-order/${workOrderId}`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching material issuances by work order:', error);
      return [];
    }
  }

  async getByRequest(requestId: string): Promise<MaterialIssuance[]> {
    try {
      const response = await apiClient.get(`/inventory/material-issuances/request/${requestId}`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching material issuances by request:', error);
      return [];
    }
  }

  async issueMaterials(data: IssueMaterialsDTO): Promise<MaterialIssuance> {
    const response = await apiClient.post('/inventory/material-issuances', data);
    return response.data;
  }

  async approve(id: string, remarks?: string): Promise<MaterialIssuance> {
    const response = await apiClient.post(`/inventory/material-issuances/${id}/approve`, {
      remarks,
    });
    return response.data;
  }

  async rejectApproval(
    id: string,
    payload: { rejection_reason_code?: string; remarks?: string }
  ): Promise<MaterialIssuance> {
    const response = await apiClient.post(
      `/inventory/material-issuances/${id}/reject-approval`,
      payload
    );
    return response.data;
  }
}

export const materialIssuanceService = new MaterialIssuanceService();
export default materialIssuanceService;
